import { useId, useLayoutEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useAppliedFilterRemoveFocus } from '../../hooks/useAppliedFilterRemoveFocus';
import { cn } from '../../styles/lib/utils';
import {
  BOOLEAN_OPTIONS,
  type FilterOperatorType,
  type FilterType,
  type FilterValueConfig,
  getFilterOperatorTypes,
  NULL_OPERATOR_IDS,
  OPERATOR_TYPES,
} from '../../utils/filterSettings';
import { AppliedFilter } from '../AppliedFilter';
import { Button, IconButton } from '../Button';
import { ComboBox } from '../ComboBox';
import { DatePicker } from '../DatePicker';
import type { DatePickerValue } from '../DatePicker/utils';
import { Form, FormField } from '../Form';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Label } from '../Label';
import { RadioBar, RadioBarItem } from '../RadioBar';
import { Switch } from '../Switch';
import { useColumnFilter } from './ColumnFilterContext';

type PinPosition = 'left' | 'right' | 'none';
type SortDirection = 'asc' | 'desc' | 'none';
type ColumnMoveDirection = 'left' | 'right';
type ColumnResizeAction = 'narrower' | 'wider' | 'reset';
type ColumnMenuAction = ColumnMoveDirection | ColumnResizeAction;

// Each step button falls back to its pair when a press disables it at an edge or size limit.
const PAIRED_ACTION: Partial<Record<ColumnMenuAction, ColumnMenuAction>> = {
  left: 'right',
  right: 'left',
  narrower: 'wider',
  wider: 'narrower',
  reset: 'narrower',
};

const getDefaultOperatorType = (filterValueConfig?: FilterValueConfig): FilterOperatorType | undefined => {
  if (filterValueConfig?.type === 'boolean') {
    return OPERATOR_TYPES.BOOLEAN[0];
  }
  return undefined;
};

type FilterFormValues = {
  operatorType: FilterOperatorType | undefined;
  value: string;
};

type ColumnMenuProps = {
  /** Column name added to each control's accessible name, so screen readers say which column it acts on. */
  columnLabel?: string;
  canHide?: boolean;
  canPin?: boolean;
  canSort?: boolean;
  canFilter?: boolean;
  showColumn?: boolean;
  onShowColumnChange?: (checked: boolean) => void;
  pinPosition?: PinPosition;
  onPinPositionChange?: (position: PinPosition) => void;
  sortDirection?: SortDirection;
  onSortDirectionChange?: (direction: SortDirection) => void;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  /** Single-pointer alternative to dragging the header; shows the Move row when set. */
  onMove?: (direction: ColumnMoveDirection) => void;
  canNarrow?: boolean;
  canWiden?: boolean;
  /** Shows the reset button; set when the column width differs from its default. */
  canResetWidth?: boolean;
  /** Single-pointer alternative to dragging the resize handle; shows the Width row when set. */
  onResize?: (action: ColumnResizeAction) => void;
  closePopover?: () => void;
  className?: string;
  testIdPrefix?: string;
};

function ColumnMenu({
  columnLabel,
  canHide,
  canPin,
  canSort,
  canFilter,
  showColumn,
  onShowColumnChange,
  pinPosition,
  onPinPositionChange,
  sortDirection,
  onSortDirectionChange,
  canMoveLeft = false,
  canMoveRight = false,
  onMove,
  canNarrow = false,
  canWiden = false,
  canResetWidth = false,
  onResize,
  closePopover,
  className,
  testIdPrefix = 'column-menu',
}: ColumnMenuProps) {
  const moveLabelId = useId();
  const widthLabelId = useId();

  const actionButtons = useRef(new Map<ColumnMenuAction, HTMLButtonElement>());
  const pendingFocus = useRef<ColumnMenuAction | null>(null);
  const registerActionButton = (action: ColumnMenuAction) => (button: HTMLButtonElement | null) => {
    if (button) actionButtons.current.set(action, button);
    else actionButtons.current.delete(action);
  };

  // Moves focus to the paired button when a press disables or removes the pressed one, which would drop focus to the body.
  useLayoutEffect(() => {
    const pressed = pendingFocus.current;
    if (!pressed) return;
    pendingFocus.current = null;
    const button = actionButtons.current.get(pressed);
    if (button && !button.disabled) return;
    const pair = PAIRED_ACTION[pressed];
    const fallback = pair ? actionButtons.current.get(pair) : undefined;
    if (fallback && !fallback.disabled) fallback.focus();
  });

  const handleMove = (direction: ColumnMoveDirection) => {
    pendingFocus.current = direction;
    onMove?.(direction);
  };

  const handleResize = (action: ColumnResizeAction) => {
    pendingFocus.current = action;
    onResize?.(action);
  };
  // Get filter configuration from context (provided by TableHeader)
  const columnFilterContext = useColumnFilter();
  const filterValueConfig = columnFilterContext?.filterValueConfig;
  const currentFilters = columnFilterContext?.currentFilters || [];
  const matchedFilterType: FilterType = {
    id: columnFilterContext?.filterId ?? '',
    label: columnFilterContext?.filterLabel ?? '',
    filterValueConfig: columnFilterContext?.filterValueConfig ?? undefined,
    filterOperatorTypeConfig: columnFilterContext?.filterOperatorTypeConfig ?? undefined,
  };
  const onAddFilter = columnFilterContext?.onAddFilter;
  const onRemoveFilter = columnFilterContext?.onRemoveFilter;

  const columnName = columnLabel || columnFilterContext?.filterLabel || '';
  const withColumn = (action: string) => (columnName ? `${action} ${columnName}` : action);
  const moveTargetName = columnName || 'column';
  const showColumnId = useId();

  // Use column-specific operator types, or TEXT as default
  const availableOperatorTypes = getFilterOperatorTypes(matchedFilterType);

  const getDefaults = (): FilterFormValues => ({
    operatorType: getDefaultOperatorType(filterValueConfig),
    value: '',
  });

  const form = useForm<FilterFormValues>({ defaultValues: getDefaults() });
  const firstFilterFieldRef = useRef<HTMLButtonElement>(null);
  const removeFocus = useAppliedFilterRemoveFocus(firstFilterFieldRef);
  const operatorType = useWatch({ control: form.control, name: 'operatorType' });
  const value = useWatch({ control: form.control, name: 'value' });

  const handleSubmit = (values: FilterFormValues) => {
    const isNullOperator = values.operatorType && NULL_OPERATOR_IDS.has(values.operatorType.id);
    if (values.operatorType && (isNullOperator || values.value.trim() !== '')) {
      onAddFilter?.({
        operatorType: values.operatorType,
        value: isNullOperator ? '' : values.value,
      });

      form.reset(getDefaults());
      closePopover?.();
    }
  };

  const isNullOperator = !!operatorType && NULL_OPERATOR_IDS.has(operatorType.id);
  const isFilterComplete = !!operatorType && (isNullOperator || value.trim() !== '');
  const hasAnyValue = !!operatorType || value.trim() !== '';

  return (
    <div className={cn('flex min-w-[161px] flex-col gap-2', className)}>
      {/* Show Column */}
      {canHide && (
        <div className="flex w-full items-center justify-between">
          <Label htmlFor={showColumnId} className="text-primary text-xs font-normal">
            Show Column
          </Label>
          <Switch
            id={showColumnId}
            aria-label={withColumn('Show Column')}
            checked={showColumn}
            onCheckedChange={onShowColumnChange}
            data-testid={`${testIdPrefix}-show-column-switch`}
          />
        </div>
      )}

      {/* Sort */}
      {canSort && (
        <div className="flex w-full items-center justify-between">
          <Label className="text-primary text-xs font-normal">Sort</Label>
          <RadioBar aria-label={withColumn('Sort')} value={sortDirection} onValueChange={onSortDirectionChange}>
            <RadioBarItem value="none" aria-label="No sort" data-testid={`${testIdPrefix}-sort-none`}>
              <Icon icon="circleBackslash" size={14} />
            </RadioBarItem>
            <RadioBarItem value="asc" aria-label="Sort ascending" data-testid={`${testIdPrefix}-sort-asc`}>
              <Icon icon="arrowUp" size={14} />
            </RadioBarItem>
            <RadioBarItem value="desc" aria-label="Sort descending" data-testid={`${testIdPrefix}-sort-desc`}>
              <Icon icon="arrowDown" size={14} />
            </RadioBarItem>
          </RadioBar>
        </div>
      )}

      {/* Pin */}
      {canPin && (
        <div className="flex w-full items-center justify-between">
          <Label className="text-primary text-xs font-normal">Pin</Label>
          <RadioBar
            aria-label={withColumn('Pin')}
            value={pinPosition}
            onValueChange={(value: PinPosition) => onPinPositionChange?.(value)}
          >
            <RadioBarItem value="none" aria-label="No pin" data-testid={`${testIdPrefix}-pin-none`}>
              <Icon icon="circleBackslash" size={14} />
            </RadioBarItem>
            <RadioBarItem value="left" aria-label="Pin left" data-testid={`${testIdPrefix}-pin-left`}>
              <Icon icon="arrowLeft" size={14} />
            </RadioBarItem>
            <RadioBarItem value="right" aria-label="Pin right" data-testid={`${testIdPrefix}-pin-right`}>
              <Icon icon="arrowRight" size={14} />
            </RadioBarItem>
          </RadioBar>
        </div>
      )}

      {/* Move */}
      {onMove && (
        <div className="flex w-full items-center justify-between">
          <Label id={moveLabelId} className="text-primary text-xs font-normal">
            Move
          </Label>
          <fieldset aria-labelledby={moveLabelId} className="m-0 flex min-w-0 items-center gap-0.5 border-0 p-0">
            <IconButton
              ref={registerActionButton('left')}
              icon="arrowLeft"
              iconSize={16}
              size="icon"
              variant="hollow"
              aria-label={`Move ${moveTargetName} left`}
              disabled={!canMoveLeft}
              onClick={() => handleMove('left')}
              data-testid={`${testIdPrefix}-move-left`}
            />
            <IconButton
              ref={registerActionButton('right')}
              icon="arrowRight"
              iconSize={16}
              size="icon"
              variant="hollow"
              aria-label={`Move ${moveTargetName} right`}
              disabled={!canMoveRight}
              onClick={() => handleMove('right')}
              data-testid={`${testIdPrefix}-move-right`}
            />
          </fieldset>
        </div>
      )}

      {/* Width */}
      {onResize && (
        <div className="flex w-full items-center justify-between">
          <Label id={widthLabelId} className="text-primary text-xs font-normal">
            Width
          </Label>
          <fieldset aria-labelledby={widthLabelId} className="m-0 flex min-w-0 items-center gap-0.5 border-0 p-0">
            {canResetWidth && (
              <IconButton
                ref={registerActionButton('reset')}
                icon="reset"
                iconSize={16}
                size="icon"
                variant="hollow"
                aria-label={`Reset ${moveTargetName} width`}
                onClick={() => handleResize('reset')}
                data-testid={`${testIdPrefix}-width-reset`}
              />
            )}
            <IconButton
              ref={registerActionButton('narrower')}
              icon="minus"
              iconSize={16}
              size="icon"
              variant="hollow"
              aria-label={`Make ${moveTargetName} narrower`}
              disabled={!canNarrow}
              onClick={() => handleResize('narrower')}
              data-testid={`${testIdPrefix}-width-narrower`}
            />
            <IconButton
              ref={registerActionButton('wider')}
              icon="plus"
              iconSize={16}
              size="icon"
              variant="hollow"
              aria-label={`Make ${moveTargetName} wider`}
              disabled={!canWiden}
              onClick={() => handleResize('wider')}
              data-testid={`${testIdPrefix}-width-wider`}
            />
          </fieldset>
        </div>
      )}

      {/* Filter Section */}
      {canFilter && onAddFilter && (
        <>
          {/* Active Filters Display */}
          {currentFilters.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label className="text-primary text-xs font-normal">Active Filters</Label>
              <div ref={removeFocus.rowRef} className="flex flex-col gap-1">
                {currentFilters.map((filter) => (
                  <AppliedFilter
                    key={filter.id}
                    filter={filter}
                    matchedFilterType={matchedFilterType}
                    onClose={() => {
                      removeFocus.beforeRemove();
                      onRemoveFilter?.(filter.id);
                    }}
                    hideColumnName
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add New Filter Form */}
          <Form form={form} onSubmit={handleSubmit} className="flex flex-col gap-2">
            <Label className="text-primary text-xs font-normal">
              {currentFilters.length > 0 ? 'Add Filter' : 'Filter'}
            </Label>

            {/* Operator Type Selector */}
            {filterValueConfig?.type !== 'boolean' && (
              <FormField
                control={form.control}
                name="operatorType"
                render={({ field }) => (
                  <ComboBox
                    ref={firstFilterFieldRef}
                    options={availableOperatorTypes.map((ot) => ({
                      value: ot.id,
                      label: ot.label,
                    }))}
                    value={field.value?.id || ''}
                    onValueChange={(val) => {
                      const selected = availableOperatorTypes.find((ot) => ot.id === val);
                      field.onChange(selected);
                      // drop value when switching to null check operator
                      if (selected && NULL_OPERATOR_IDS.has(selected.id)) {
                        form.setValue('value', '');
                      }
                    }}
                    placeholder="Select operator..."
                    aria-label={`${withColumn('Filter')} operator`}
                    data-testid={`${testIdPrefix}-operator-select`}
                    searchPlaceholder="Search operators..."
                    emptyText="No operator found."
                    triggerClassName="h-6 w-full"
                  />
                )}
              />
            )}

            {/* Value Field - type depends on filterValueConfig */}
            {operatorType && !NULL_OPERATOR_IDS.has(operatorType.id) && (
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <>
                    {filterValueConfig?.type === 'select' ? (
                      <ComboBox
                        options={filterValueConfig.options}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select..."
                        searchPlaceholder="Search options..."
                        aria-label={`${withColumn('Filter')} value`}
                        data-testid={`${testIdPrefix}-value-select`}
                        emptyText="No option found."
                        triggerClassName="h-6 w-full"
                      />
                    ) : filterValueConfig?.type === 'boolean' ? (
                      <ComboBox
                        ref={firstFilterFieldRef}
                        options={BOOLEAN_OPTIONS}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select..."
                        isSearchable={false}
                        aria-label={`${withColumn('Filter')} value`}
                        data-testid={`${testIdPrefix}-value-boolean`}
                        triggerClassName="h-6 w-full"
                      />
                    ) : filterValueConfig?.type === 'date' ? (
                      (() => {
                        const dateValue = field.value;
                        let pickerValue: DatePickerValue = null;
                        if (dateValue) {
                          try {
                            const date = new Date(dateValue);
                            if (!Number.isNaN(date.getTime())) {
                              pickerValue = {
                                date: date.toISOString(),
                                view: 'Day',
                              };
                            }
                          } catch {}
                        }
                        return (
                          <DatePicker
                            value={pickerValue}
                            onChange={(value) => {
                              if (value && 'date' in value) {
                                field.onChange(value.date);
                              } else {
                                field.onChange('');
                              }
                            }}
                            allowRange={false}
                            multiView={false}
                            placeholder="MM/DD/YYYY"
                            className="w-full"
                            inputClassName="h-6 w-full"
                            aria-label={`${withColumn('Filter')} value`}
                            data-testid={`${testIdPrefix}-value-date`}
                          />
                        );
                      })()
                    ) : (
                      <Input
                        type="text"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="Enter value..."
                        aria-label={`${withColumn('Filter')} value`}
                        data-testid={`${testIdPrefix}-value-input`}
                        className="h-6 w-full"
                      />
                    )}
                  </>
                )}
              />
            )}

            {/* Filter Actions */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={() => form.reset(getDefaults())}
                className="h-6 flex-1 text-xs"
                disabled={!hasAnyValue}
                data-testid={`${testIdPrefix}-clear-button`}
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="small"
                className="h-6 flex-1 text-xs"
                disabled={!isFilterComplete}
                data-testid={`${testIdPrefix}-apply-button`}
              >
                {currentFilters.length > 0 ? 'Add' : 'Apply'}
              </Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
}

export {
  ColumnMenu,
  type ColumnMenuProps,
  type ColumnMoveDirection,
  type ColumnResizeAction,
  type PinPosition,
  type SortDirection,
};
